// Module-scoped lazy loader for Google reCAPTCHA v3 (score-based).
// Loaded on first form engagement (focus/pointer/keypress) as a pure
// side-effect — never via a React Provider, never via a tree switch.
// Required by the lovable-email-form skill to keep focus stable on
// first interaction (Provider mount/unmount reparents inputs).
//
// The lead service verifies tokens with the Classic `siteverify` endpoint, so
// this must load Classic `api.js` and call `grecaptcha.execute` — Enterprise
// tokens are rejected by that verifier.

import { RECAPTCHA_SITE_KEY } from "@/lib/recaptcha-site-key";

const SITE_KEY = RECAPTCHA_SITE_KEY;

type GrecaptchaV3 = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
};

function getGrecaptcha(): GrecaptchaV3 | undefined {
  if (typeof window === "undefined") return undefined;
  const g = (window as unknown as { grecaptcha?: GrecaptchaV3 }).grecaptcha;
  return g && typeof g.execute === "function" ? g : undefined;
}

let scriptPromise: Promise<void> | null = null;

export function loadRecaptcha(): Promise<void> {
  if (!SITE_KEY) {
    return Promise.reject(new Error("recaptcha-site-key-missing"));
  }
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    if (typeof document === "undefined") {
      reject(new Error("recaptcha-no-document"));
      return;
    }
    if (getGrecaptcha()) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      "script[data-recaptcha-v3]",
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("recaptcha-script-load-failed")),
        { once: true },
      );
      return;
    }
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaV3 = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("recaptcha-script-load-failed"));
    document.body.appendChild(script);
  });
  return scriptPromise;
}

export async function executeRecaptcha(
  action: string,
  timeoutMs = 4000,
): Promise<string> {
  await Promise.race([
    loadRecaptcha(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("recaptcha-timeout")), timeoutMs),
    ),
  ]);
  const g = getGrecaptcha();
  if (!g) throw new Error("recaptcha-not-ready");
  await new Promise<void>((resolve) => g.ready(() => resolve()));
  return g.execute(SITE_KEY, { action });
}
